import { ShippingFlagKey } from "@redotech/feature-flags-keys-shipping/keys";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { REDO_EFFECT_API_URL } from "@redotech/redo-merchant-app-common/config";
import { EffectApiClientProvider } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { BrowserNotificationProvider } from "@redotech/redo-merchant-app-common/notifications/browser-notification-provider";
import { registerOpenOrderNavigate } from "@redotech/redo-merchant-app-common/open-order-notifications";
import { RouterProtector } from "@redotech/redo-merchant-app-common/routing/router-protector";
import { getStoreScopedPath } from "@redotech/redo-merchant-app-common/routing/store-scope";
import { ShippingNewSettingsContext } from "@redotech/redo-merchant-app-common/shipping-new-settings-context";
import { StreamProvider } from "@redotech/redo-merchant-app-common/stream/stream-provider";
import { StreamingRouteTracker } from "@redotech/redo-merchant-app-common/streaming-enabled";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  DocumentTitleOverriders,
  DocumentTitleOverridersContext,
} from "@redotech/redo-merchant-app-common/title";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { WalkthroughProvider } from "@redotech/redo-merchant-app-common/walkthrough/walkthrough-context";
import { BillingFailedAlert } from "@redotech/redo-merchant-general-app/billing/failed-alert";
import { ShopifyReturnsBillingDeadlineAlert } from "@redotech/redo-merchant-general-app/billing/shopify-returns-billing-deadline-alert";
import {
  TourProvider,
  useTourStepKey,
} from "@redotech/redo-merchant-general-app/tour/tour-context";
import { VoiceSessionHost } from "@redotech/redo-merchant-support-app/v2/voice/voice-session-host";
import { ArbiterDesignSystemInspector } from "@redotech/redo-web/arbiter-design-system-inspector/arbiter-design-system-inspector";
import {
  BreadcrumbOverriders,
  BreadcrumbOverridersContext,
  useBreadcrumbs,
} from "@redotech/redo-web/breadcrumb";
import { SettingsPresentationContext } from "@redotech/redo-web/settings-page-layout/settings-presentation-context";
import { ThursdayBootWarningBanner } from "@redotech/redo-web/thursday-boot-warning-banner";
import { memo, useContext, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDocumentTitle } from "usehooks-ts";
import { usesRedesignedSettingsControls } from "./app/navigation/routing/settings-control-style";
import { OrderSyncProgress } from "./order-sync-progress";
import { RouteStateReporter } from "./platform/route-state-marker";
import { WALKTHROUGH_REGISTRY } from "./walkthrough/walkthrough-registry";

// Remounts the routed subtree when the mock-preview tour advances, so a step
// that reuses the previous step's route still refetches with its new fixture.
const TourKeyedOutlet = memo(function TourKeyedOutlet() {
  const stepKey = useTourStepKey();
  return <Outlet key={stepKey ?? "no-tour"} />;
});

const RouterRootInner = memo(function RouterRootInner() {
  const navigate = useNavigate();
  useEffect(() => registerOpenOrderNavigate(navigate), [navigate]);
  const { teamId } = useParams();
  const url = useLocation();
  const user = useContext(UserContext);
  const team = useContext(TeamContext);

  const auth = useContext(AuthContext);

  useMemo(() => {
    if (auth && auth.teamId && !teamId) {
      const isTokenLogin = url.pathname === "/login/token";

      if (isTokenLogin) {
        const urlParams = new URLSearchParams(url.search);
        const nextParam = urlParams.get("next") || "";

        navigate(getStoreScopedPath(auth.teamId, nextParam), { replace: true });
      } else {
        navigate(getStoreScopedPath(auth.teamId, url.pathname + url.search), {
          replace: true,
        });
      }
    }
  }, [auth, teamId, url.pathname, navigate, url.search]);

  const breadcrumbs = useBreadcrumbs();

  const [documentTitleOverriders] = useContext(DocumentTitleOverridersContext);
  const title = useMemo(() => {
    const firstOverrider = Array.from(
      documentTitleOverriders?.values() ?? [],
    )[0];
    if (firstOverrider) {
      return firstOverrider(breadcrumbs || []);
    }
    return breadcrumbs
      ? `${breadcrumbs.map((breadcrumb) => breadcrumb.name).join(" ")} — Redo`
      : "Redo";
  }, [breadcrumbs, documentTitleOverriders]);

  useDocumentTitle(title);

  return (
    <StreamingRouteTracker>
      <EffectApiClientProvider
        authToken={auth?.token}
        baseUrl={REDO_EFFECT_API_URL}
      >
        <StreamProvider>
          <BrowserNotificationProvider>
            <WalkthroughProvider registry={WALKTHROUGH_REGISTRY}>
              <TourProvider>
                <ThursdayBootWarningBanner
                  email={user?.email}
                  teamId={team?._id}
                />
                <ArbiterDesignSystemInspector email={user?.email} />
                <OrderSyncProgress />
                <BillingFailedAlert />
                <ShopifyReturnsBillingDeadlineAlert />
                <RouterProtector>
                  <VoiceSessionHost>
                    <TourKeyedOutlet />
                  </VoiceSessionHost>
                </RouterProtector>
              </TourProvider>
            </WalkthroughProvider>
          </BrowserNotificationProvider>
        </StreamProvider>
      </EffectApiClientProvider>
    </StreamingRouteTracker>
  );
});

export const RouterRoot = memo(function RouterRoot() {
  const team = useContext(TeamContext);
  const location = useLocation();
  const shippingNewSettings =
    team?.featureFlags?.[ShippingFlagKey.SHIPPING_NEW_SETTINGS] === true;
  const redesignedSettingsControls =
    shippingNewSettings && usesRedesignedSettingsControls(location.pathname);
  const [breadcrumbOverriders, setBreadcrumbOverriders] =
    useState<BreadcrumbOverriders>(new Map());
  const [documentTitleOverriders, setDocumentTitleOverriders] =
    useState<DocumentTitleOverriders>(new Map());
  return (
    <ShippingNewSettingsContext.Provider value={shippingNewSettings}>
      <SettingsPresentationContext.Provider
        value={redesignedSettingsControls ? "redesigned" : "legacy"}
      >
        <BreadcrumbOverridersContext.Provider
          value={[breadcrumbOverriders, setBreadcrumbOverriders]}
        >
          <DocumentTitleOverridersContext.Provider
            value={[documentTitleOverriders, setDocumentTitleOverriders]}
          >
            <RouteStateReporter />
            <RouterRootInner />
          </DocumentTitleOverridersContext.Provider>
        </BreadcrumbOverridersContext.Provider>
      </SettingsPresentationContext.Provider>
    </ShippingNewSettingsContext.Provider>
  );
});
